import { useApolloClient } from '@apollo/react-hooks';
import c from 'classnames';
import { graphqlApiSite } from 'core/api/graphql';
import { getPictureUrl } from 'core/api/pictures';
import { useLinks } from 'core/components/linksContext';
import { useSettings } from 'core/components/settingsContext';
import { showSnackbar } from 'core/components/snackbar';
import { title as titleStyle } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatTimeSpanHHMM, isHtmlEmpty } from 'core/utils';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { VideoCourse } from 'site/graphql';
import { formItemCardStyles } from './formItemCardStyles';
import ItemCardMenu, { SET_VIEWED } from './ItemCardMenu';

type IPrivateProductVideoCourseCardProps = {
    videoCourse: Pick<VideoCourse, 'id' | 'description' | 'title' | 'hours' | 'isViewed' | 'link' | 'pictureId'>;
    noAccess: boolean;
} & WithStyles<typeof styles>;

const productFormType = 'VIDEO_COURSE';
const typename = 'videoCourse';

const PrivateProductVideoCourseCard: React.FC<IPrivateProductVideoCourseCardProps> = ({
    classes,
    noAccess,
    videoCourse,
}) => {
    const settings = useSettings();
    const client = useApolloClient();
    const links = useLinks();
    const history = useHistory();
    const { description, title, hours, isViewed, link, id } = videoCourse;

    const handleClick = () => {
        if (noAccess) {
            history.push(links.courseList.url());
            return;
        }

        if (link) {
            window.open(`${link}`, '_blank');

            // помечаем форм итем просмотренным если был непросмотреным
            if (!isViewed) {
                const query = `mutation {
                        markFormItemAsViewedOrUnviewed(productFormType: ${productFormType}, formItemId: ${id})
                    }`;
                graphqlApiSite<{ id: number }>(query, null)
                    .then(() => {
                        client.writeQuery({
                            query: SET_VIEWED,
                            variables: { id },
                            data: {
                                setViewed: { id, isViewed: true, __typename: typename },
                            },
                        });
                    })
                    .catch(error => {
                        showSnackbar({ snackbarErrors: error });
                    });
            }
        } else {
            showSnackbar({
                snackbarErrors: [
                    {
                        message: `Ссылка на видеокурс будет доступна позднее`,
                    },
                ],
            });
        }
    };

    return (
        <div className={c(classes.root, { [classes.darkenedRoot]: noAccess })}>
            <div className={classes.greyRect} onClick={handleClick}>
                <div className={classes.playButton} />
                <div
                    className={classes.videoPreviewImage}
                    style={{
                        background: `url("${
                            videoCourse.pictureId
                                ? getPictureUrl(videoCourse.pictureId, '370x180', settings)
                                : require('./video_image.png')
                        }") center center / cover no-repeat`,
                        backgroundColor: '#333333',
                        opacity: isViewed ? 0.5 : 1,
                    }}
                />
                {isViewed && <div className={classes.viewedItem}>Видеозапись просмотрена</div>}
            </div>
            <div className={classes.content}>
                <div className={classes.contentAndDot}>
                    <div className={classes.info}>
                        <h3 className={classes.title}>{title}</h3>
                        {hours && <div className={classes.hours}>Продолжительность: {formatTimeSpanHHMM(hours)}</div>}
                        <div
                            className={classes.shortDesc}
                            dangerouslySetInnerHTML={{ __html: !isHtmlEmpty(description) ? description : '' }}
                        />
                    </div>
                    <ItemCardMenu eventRecord={videoCourse} productFormType={productFormType} typename={typename} />
                </div>
                <a className={classes.button} onClick={handleClick}>
                    {noAccess ? 'Открыть доступ' : isViewed ? 'Смотреть еще раз' : 'Смотреть'}
                </a>
            </div>
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        ...formItemCardStyles(theme),
        title: {
            ...titleStyle({
                size: 'h3',
                color: 'black',
            }),
            fontSize: '1.15rem',
            marginBottom: '1.25rem',
        },
        hours: {
            fontSize: 0.9 + 'rem',
            marginTop: '-1rem',
            marginBottom: '1rem',
        },
        contentAndDot: {
            position: 'relative',
            display: 'flex',
        },
        info: {
            flexGrow: 1,
        },
    });

export default injectSheet(styles, 'PrivateProductVideoCourseCard')(PrivateProductVideoCourseCard);
